import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { emailWithTldValidator } from 'src/app/validators/email-tld.validator';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IUser } from 'src/app/models/user.model';
import { IError } from 'src/app/models/error.model';
import { UserService } from 'src/app/services/user.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { ContextService } from 'src/app/services/context.service';
import { IClinicContext } from 'src/app/models/clinic-context.model';
import { generatePassword, copyToClipboard } from 'src/app/utils/password.utils';

@Component({
  selector: 'app-user-form-v2',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.sass']
})
export class UserFormV2Component implements OnInit {
  loading = false;
  userForm!: FormGroup;
  generatedPassword = '';
  passwordCopied = false;

  // Clinics multi-select
  availableClinics: IClinicContext[] = [];
  selectedClinicIds: string[] = [];
  defaultClinicId = '';
  clinicsDropdownOpen = false;
  activeClinicHasGroup = false;
  // Search term for clinics dropdown
  clinicSearchTerm = '';

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private store: Store<{ warningPopup: IError }>,
    private contextService: ContextService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadAvailableClinics();
    this.generatedPassword = generatePassword();
  }

  initForm(): void {
    this.userForm = this.fb.group({
      name: ['', [Validators.required]],
  email: ['', [Validators.required, emailWithTldValidator()]],
      is_blocked: ['false', [Validators.required]],
      permission: ['', [Validators.required]]
    });

    // Limpa clínicas selecionadas ao trocar permissão para ADMIN
    this.userForm.get('permission')?.valueChanges.subscribe(value => {
      if (value !== 'ATTENDANT') {
        this.selectedClinicIds = [];
        this.defaultClinicId = '';
        this.clinicsDropdownOpen = false;
      }
    });
  }

  // ─── Clinics Multi-Select ────────────────────────────────────────────

  loadAvailableClinics(): void {
    this.contextService.loadClinicsAndSetDefault(true).subscribe({
      next: () => {
        const allClinics = this.contextService.getAvailableClinics();
        const activeClinic = this.contextService.getActiveClinicSnapshot();
        this.activeClinicHasGroup = !!activeClinic?.groupId;
        // Filtra apenas clínicas do mesmo grupo da clínica ativa
        if (activeClinic?.groupId) {
          this.availableClinics = allClinics.filter(c => c.groupId === activeClinic.groupId);
        } else {
          // Sem grupo — mostra apenas a clínica ativa
          this.availableClinics = allClinics.filter(c => c.cnpj === activeClinic?.cnpj);
        }
        // Pré-seleciona a clínica ativa
        if (activeClinic) {
          const clinicId = activeClinic.id || activeClinic.cnpj;
          if (clinicId && !this.selectedClinicIds.includes(clinicId)) {
            this.selectedClinicIds = [clinicId];
            this.defaultClinicId = clinicId;
          }
        } else if (this.availableClinics.length === 1) {
          // Se há apenas uma clínica, pré-seleciona automaticamente
          this.selectedClinicIds = [this.availableClinics[0].id];
          this.defaultClinicId = this.availableClinics[0].id;
        }
      }
    });
  }

  toggleClinicSelection(clinicId: string): void {
    const index = this.selectedClinicIds.indexOf(clinicId);
    if (index > -1) {
      this.selectedClinicIds.splice(index, 1);
      // Se a clínica padrão foi desmarcada, reseta
      if (this.defaultClinicId === clinicId) {
        this.defaultClinicId = this.selectedClinicIds.length > 0 ? this.selectedClinicIds[0] : '';
      }
    } else {
      this.selectedClinicIds.push(clinicId);
      // Se é a primeira seleção, define como padrão automaticamente
      if (this.selectedClinicIds.length === 1) {
        this.defaultClinicId = clinicId;
      }
    }
  }

  isClinicSelected(clinicId: string): boolean {
    return this.selectedClinicIds.includes(clinicId);
  }

  get filteredClinics(): IClinicContext[] {
    const term = (this.clinicSearchTerm || '').toLowerCase().trim();
    if (!term) return this.availableClinics;
    return this.availableClinics.filter(c =>
      (c.name || '').toLowerCase().includes(term) ||
      (c.cnpj || '').toLowerCase().includes(term) ||
      (c.id || '').toLowerCase().includes(term)
    );
  }

  get selectedClinicsNames(): string {
    if (this.selectedClinicIds.length === 0) return '';
    return this.availableClinics
      .filter(c => this.selectedClinicIds.includes(c.id))
      .map(c => c.name)
      .join(', ');
  }

  get selectedClinicsForDefault(): IClinicContext[] {
    return this.availableClinics.filter(c => this.selectedClinicIds.includes(c.id));
  }

  toggleClinicsDropdown(): void {
    this.clinicsDropdownOpen = !this.clinicsDropdownOpen;
  }

  onCancel(): void {
    this.router.navigate(['main', 'v2', 'users']);
  }

  regeneratePassword(): void {
    this.generatedPassword = generatePassword();
    this.passwordCopied = false;
  }

  async copyPassword(): Promise<void> {
    if (this.generatedPassword) {
      const success = await copyToClipboard(this.generatedPassword);
      if (success) {
        this.passwordCopied = true;
        setTimeout(() => this.passwordCopied = false, 2000);
      }
    }
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      this.store.dispatch(setWarningText('Preencha e valide todos os campos obrigatórios.'));
      return;
    }

    // Validação de clínicas (apenas para Atendente com grupo)
    const isAttendant = this.userForm.value.permission === 'ATTENDANT';
    if (isAttendant && this.activeClinicHasGroup) {
      if (this.selectedClinicIds.length === 0) {
        this.store.dispatch(setWarningText('Selecione pelo menos uma clínica.'));
        return;
      }
      if (!this.defaultClinicId) {
        this.store.dispatch(setWarningText('Defina uma clínica padrão.'));
        return;
      }
    } else if (isAttendant && !this.activeClinicHasGroup) {
      // Sem grupo — vincula automaticamente à clínica ativa
      const activeClinic = this.contextService.getActiveClinicSnapshot();
      if (activeClinic) {
        const clinicId = activeClinic.id || activeClinic.cnpj;
        this.selectedClinicIds = [clinicId];
        this.defaultClinicId = clinicId;
      }
    }

    this.loading = true;
    const formValue = this.userForm.value;

    const userRequest: IUser = {
      name: formValue.name?.trim() || '',
      email: formValue.email?.trim().toLowerCase() || '',
      permission: formValue.permission || '',
      is_blocked: formValue.is_blocked === 'true',
      password: this.generatedPassword
    };

    this.userService.registerUser(userRequest).subscribe({
      next: (createdUser) => {
        // Se for ADMIN, não precisa vincular clínicas
        if (!isAttendant) {
          this.loading = false;
          this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
            this.router.navigate(['main', 'v2', 'users']);
          });
          return;
        }
        // Após criar o usuário, vincular as clínicas
        const userId = createdUser.id || '';
        this.userService.updateUserAccess(userId, this.selectedClinicIds, this.defaultClinicId).subscribe({
          next: () => {
            this.loading = false;
            this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
              this.router.navigate(['main', 'v2', 'users']);
            });
          },
          error: (error) => {
            console.error('Erro ao vincular clínicas ao usuário:', error);
            // Usuário foi criado, mas falhou ao vincular clínicas — redireciona assim mesmo
            this.loading = false;
            this.store.dispatch(setWarningText('Usuário criado, mas houve erro ao vincular clínicas. Edite o usuário para corrigir.'));
            this.router.navigate(['main', 'v2', 'users']);
          }
        });
      },
      error: (error) => {
        console.error('Erro ao criar usuário:', error);
        const message = error?.error?.message || error?.message || 'Erro ao criar usuário.';
        const status = error?.status || 0;
        this.store.dispatch(setWarningText(message, status));
        this.loading = false;
      }
    });
  }
}
